import React, { useEffect, useState } from "react";
import {
  FaUserShield,
  FaUser,
  FaEnvelope,
  FaEdit,
  FaSave,
  FaTimes,
  FaCheckCircle,
  FaCalendarAlt,
  FaSpinner,
} from "react-icons/fa";
import "./Profile.css";

import { BASE_URL } from "../../Config/config";

export default function Profile() {
  const [profile, setProfile] = useState({
    id: "",
    username: "",
    name: "",
    email: "",
    status: 0,
    created_at: "",
  });

  const [editData, setEditData] = useState({
    name: "",
    email: "",
    username: "",
  });

  const [isEditing, setIsEditing] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  /*
  |--------------------------------------------------------------------------
  | LOAD PROFILE
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    setError("");

    const adminId = sessionStorage.getItem("admin_id");

    if (!adminId) {
      setError("Admin session not found. Please login again.");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${BASE_URL}/admin_auth.php?action=get_profile&id=${adminId}`,
      );

      const data = await response.json();

      if (!data.success) {
        setError(data.message || "Unable to load profile.");
        return;
      }

      const admin = data.admin;

      setProfile(admin);

      setEditData({
        name: admin.name || "",
        email: admin.email || "",
        username: admin.username || "",
      });
    } catch (err) {
      console.error(err);
      setError("Unable to connect to server.");
    } finally {
      setLoading(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | EDIT
  |--------------------------------------------------------------------------
  */

  const handleEdit = () => {
    setEditData({
      name: profile.name || "",
      email: profile.email || "",
      username: profile.username || "",
    });

    setMessage("");
    setError("");
    setIsEditing(true);
  };

  /*
  |--------------------------------------------------------------------------
  | INPUT
  |--------------------------------------------------------------------------
  */

  const handleChange = (e) => {
    setEditData({
      ...editData,
      [e.target.name]: e.target.value,
    });

    setMessage("");
    setError("");
  };

  /*
  |--------------------------------------------------------------------------
  | CANCEL
  |--------------------------------------------------------------------------
  */

  const handleCancel = () => {
    setEditData({
      name: profile.name || "",
      email: profile.email || "",
      username: profile.username || "",
    });

    setMessage("");
    setError("");
    setIsEditing(false);
  };

  /*
  |--------------------------------------------------------------------------
  | SAVE PROFILE
  |--------------------------------------------------------------------------
  */

  const handleSave = async (e) => {
    e.preventDefault();

    setMessage("");
    setError("");

    if (!editData.username.trim()) {
      setError("Please enter username.");
      return;
    }

    if (!editData.name.trim()) {
      setError("Please enter admin name.");
      return;
    }

    if (!editData.email.trim()) {
      setError("Please enter email address.");
      return;
    }

    try {
      setSaving(true);

      const response = await fetch(
        `${BASE_URL}/admin_auth.php?action=update_profile`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id: profile.id,
            username: editData.username.trim(),
            name: editData.name.trim(),
            email: editData.email.trim(),
          }),
        },
      );

      const data = await response.json();

      if (!data.success) {
        setError(data.message || "Unable to update profile.");
        return;
      }

      const updatedAdmin = {
        ...profile,
        username: editData.username.trim(),
        name: editData.name.trim(),
        email: editData.email.trim(),
      };

      setProfile(updatedAdmin);

      sessionStorage.setItem("admin_username", editData.username.trim());

      sessionStorage.setItem("admin_name", editData.name.trim());
      window.dispatchEvent(new Event("admin-profile-updated"));

      sessionStorage.setItem("admin_email", editData.email.trim());

      setIsEditing(false);
      setMessage("Profile updated successfully.");
    } catch (err) {
      console.error(err);
      setError("Unable to connect to server.");
    } finally {
      setSaving(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | DATE FORMAT
  |--------------------------------------------------------------------------
  */

  const formatDate = (date) => {
    if (!date) {
      return "N/A";
    }

    const parsedDate = new Date(date);

    if (isNaN(parsedDate.getTime())) {
      return date;
    }

    return parsedDate.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  /*
  |--------------------------------------------------------------------------
  | LOADING
  |--------------------------------------------------------------------------
  */

  if (loading) {
    return (
      <div className="admin-profile-page">
        <div className="profile-loading">
          <FaSpinner className="profile-spinner" />
          <span>Loading profile...</span>
        </div>
      </div>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | UI
  |--------------------------------------------------------------------------
  */

  return (
    <div className="admin-profile-page">
      <div className="profile-container">
        {/* HEADER */}

        <div className="profile-header">
          <div className="profile-title">
            <div className="profile-title-icon">
              <FaUserShield />
            </div>

            <div>
              <h2>Admin Profile</h2>

              <p>Manage your administrator account information</p>
            </div>
          </div>

          {!isEditing && (
            <button
              type="button"
              className="profile-edit-button"
              onClick={handleEdit}
            >
              <FaEdit />
              Edit Profile
            </button>
          )}
        </div>

        {/* MESSAGE */}

        {error && <div className="profile-message error">{error}</div>}

        {message && (
          <div className="profile-message success">
            <FaCheckCircle />
            {message}
          </div>
        )}

        {/* PROFILE CARD */}

        <div className="profile-card">
          {/* PROFILE TOP */}

          <div className="profile-user-header">
            <div className="profile-avatar">
              <FaUserShield />
            </div>

            <div className="profile-user-info">
              <h3>{profile.name || "Administrator"}</h3>

              <p>@{profile.username}</p>

              <span
                className={
                  Number(profile.status) === 1
                    ? "profile-status active"
                    : "profile-status inactive"
                }
              >
                <FaCheckCircle />

                {Number(profile.status) === 1
                  ? "Active Account"
                  : "Inactive Account"}
              </span>
            </div>
          </div>

          {/* VIEW MODE */}

          {!isEditing ? (
            <div className="profile-details">
              {/* NAME */}

              <div className="profile-detail-item">
                <div className="detail-icon">
                  <FaUser />
                </div>

                <div>
                  <span>Full Name</span>
                  <strong>{profile.name || "Not provided"}</strong>
                </div>
              </div>

              {/* USERNAME */}

              <div className="profile-detail-item">
                <div className="detail-icon">
                  <FaUserShield />
                </div>

                <div>
                  <span>Username</span>
                  <strong>{profile.username}</strong>
                </div>
              </div>

              {/* EMAIL */}

              <div className="profile-detail-item">
                <div className="detail-icon">
                  <FaEnvelope />
                </div>

                <div>
                  <span>Email Address</span>
                  <strong>{profile.email || "Not provided"}</strong>
                </div>
              </div>

              {/* CREATED */}

              <div className="profile-detail-item">
                <div className="detail-icon">
                  <FaCalendarAlt />
                </div>

                <div>
                  <span>Account Created</span>
                  <strong>{formatDate(profile.created_at)}</strong>
                </div>
              </div>
            </div>
          ) : (
            /* EDIT MODE */

            <form className="profile-edit-form" onSubmit={handleSave}>
              {/* NAME */}

              <div className="profile-form-group">
                <label>Full Name</label>

                <div className="profile-input-wrapper">
                  <FaUser />

                  <input
                    type="text"
                    name="name"
                    placeholder="Enter admin name"
                    value={editData.name}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* USERNAME */}

              <div className="profile-form-group">
                <label>Username</label>

                <div className="profile-input-wrapper ">
                  <FaUserShield />
                  <input
                    type="text"
                    name="username"
                    placeholder="Enter username"
                    value={editData.username}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* EMAIL */}

              <div className="profile-form-group">
                <label>Email Address</label>

                <div className="profile-input-wrapper">
                  <FaEnvelope />

                  <input
                    type="email"
                    name="email"
                    placeholder="Enter email address"
                    value={editData.email}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* BUTTONS */}

              <div className="profile-form-buttons">
                <button
                  type="button"
                  className="profile-cancel-button"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  <FaTimes />
                  Cancel
                </button>

                <button
                  type="submit"
                  className="profile-save-button"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <FaSpinner className="profile-spinner" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FaSave />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
